import PropTypes from "prop-types";
import { anchorProductToUrl } from "../../state/modules/search/productTile/actions";
import { analyticsProductClick } from "../../state/modules/analytics/actions";
import { connect } from "react-redux";
import { getShowDiscountLabel } from "../../state/modules/featureToggles/selectors";
import { getPageSize } from "../../state/modules/search/selectors";
import { withTranslation } from "../translation";
import styles from "./index.css";
import getDiscount from "./getDiscount";
import ProductDescription from "./ProductDescription";
import ProductPrice from "./ProductPrice";
import SaveForLater from "./SaveForLater";
import Banners from "../Banners";
import ProductLink from "./ProductLink";

export const ProductTile = ({
  id,
  tileId,
  url,
  image,
  price,
  isSale,
  isOutlet,
  badgeType,
  productType,
  isSponsored,
  sponsoredCampaignId,
  sponsoredRank,
  focusOnMount,
  pageNumber,
  description,
  colourWayId,
  reducedPrice,
  formatCurrency,
  formatTranslation,
  hasMultiplePrices,
  discountLabelEnabled,
  ProductImageComponent,
  analyticsProductClick,
  anchorProductToUrl,
  isSellingFast
}) => {
  let sponsoredProps = {};
  if (isSponsored) {
    sponsoredProps = {
      sponsoredCampaignId,
      sponsoredRank
    };
  }

  const getProductTileId = id => `product-${id}`;

  const formattedPrice = formatCurrency(price);

  let formattedReducedPrice = null,
    discount = null;
  if (reducedPrice) {
    formattedReducedPrice = formatCurrency(reducedPrice);
    if (discountLabelEnabled) {
      discount = getDiscount({ oldPrice: price, newPrice: reducedPrice });
    }
  }

  return (
    <article
      id={getProductTileId(tileId)}
      data-auto-id="productTile"
      className={styles.productTile}
    >
      <ProductLink
        className={styles.productLink}
        href={url}
        focusOnMount={focusOnMount}
        description={description}
        price={formattedPrice}
        reducedPrice={formattedReducedPrice}
        isOutlet={isOutlet}
        discount={discount}
        badgeType={badgeType}
        hasMultiplePrices={hasMultiplePrices}
        formatTranslation={formatTranslation}
        isSponsored={isSponsored}
        isSellingFast={isSellingFast}
        analyticsProductClick={analyticsProductClick}
        anchorProductToUrl={anchorProductToUrl}
        productTileId={getProductTileId(id)}
        sponsoredProps={sponsoredProps}
        pageNumber={pageNumber}
      >
        <ProductImageComponent src={image} badgeType={badgeType} />
        <ProductDescription text={description} />
        <ProductPrice
          price={formattedPrice}
          isSale={isSale}
          isOutlet={isOutlet}
          reducedPrice={formattedReducedPrice}
          hasMultiplePrices={hasMultiplePrices}
        />
        <Banners
          discount={discount}
          isSponsored={isSponsored}
          isSellingFast={isSellingFast}
        />
      </ProductLink>
      {productType !== "MixMatchGroup" && (
        <SaveForLater
          productId={id}
          colourWayId={colourWayId}
          {...sponsoredProps}
        />
      )}
    </article>
  );
};

ProductTile.propTypes = {
  isSale: PropTypes.bool,
  isOutlet: PropTypes.bool,
  pageNumber: PropTypes.number,
  reducedPrice: PropTypes.number,
  id: PropTypes.number.isRequired,
  tileId: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  hasMultiplePrices: PropTypes.bool,
  image: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  discountLabelEnabled: PropTypes.bool,
  description: PropTypes.string.isRequired,
  colourWayId: PropTypes.number.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  formatTranslation: PropTypes.func.isRequired,
  anchorProductToUrl: PropTypes.func.isRequired,
  analyticsProductClick: PropTypes.func.isRequired,
  badgeType: PropTypes.oneOf(["MixMatch", "MoreColors"]),
  productType: PropTypes.string.isRequired,
  focusOnMount: PropTypes.bool.isRequired,
  ProductImageComponent: PropTypes.any.isRequired,
  isSponsored: PropTypes.bool.isRequired,
  sponsoredCampaignId: PropTypes.string,
  sponsoredRank: PropTypes.number,
  pageSize: PropTypes.number.isRequired,
  isSellingFast: PropTypes.bool
};

ProductTile.defaultProps = {
  badgeType: null,
  isSale: false,
  isOutlet: false,
  hasMultiplePrices: false,
  focusOnMount: false
};

ProductTile.displayName = "ProductTile";

const mapStateToProps = state => ({
  discountLabelEnabled: getShowDiscountLabel(state),
  pageSize: getPageSize(state)
});

const mapDispatchToProps = {
  anchorProductToUrl,
  analyticsProductClick
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation(ProductTile));
